import React, { useRef, useEffect, useState, useCallback } from 'react';
import { DiadoMes } from '../../components/Ciclo/styles';
import { Main } from '../GeraImpresso/styles';
import Header from '../../components/Header';
import { GlobalStyle } from '../../styles/global';
import { useLocation } from "react-router-dom";
import { SquemaSimbols, Simbols } from '../../components/Simbols';
import { format, parseISO } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import logo from '../../assets/metodo-billings-marca-dashboard.png';
import { useReactToPrint } from 'react-to-print';
import { useAuth } from '../../hooks/Auth';
import { api } from '../../services/api';
import Loading from '../../components/Loading';

/* Max days per row chunk before wrapping the cycle to the next group of rows */
const CHUNK_SIZE = 35;

/* Sensação row height (px) */
const SENS_H = 110;

const GeraImpresso3Ciclos: React.FC = () => {

    const location   = useLocation();
    const ref        = useRef(null);
    const userlogado = useAuth();

    const { idAllCiclos, cicloIndex, iduser: iduserState } = location.state as any;
    const iduserTarget = iduserState ?? userlogado.user.iduser;

    const [ciclosData, setCiclosData] = useState<any[][]>([]);
    const [fillerHeight, setFillerHeight] = useState(0);
    const tableRef = useRef<HTMLTableElement>(null);
    const [load, setLoad]             = useState(true);

    const total = idAllCiclos.length;

    const windowIndices: number[] = (() => {
        if (total <= 3) return Array.from({ length: total }, (_: any, k: number) => k);
        const end   = Math.min(cicloIndex + 2, total - 1);
        const start = Math.max(0, end - 2);
        return [start, start + 1, start + 2];
    })();

    const fetchCiclo = useCallback(async (idciclo: any) => {
        const resp = await api.get('/ciclos/readCiclo.php', {
            params: { iduser: iduserTarget, idciclo: idciclo.numciclo, proximo: false }
        });
        return resp.data[0] as any[];
    }, [iduserTarget]);

    

    useEffect(() => {
        async function loadAll() {
            try {
                const results = await Promise.all(
                    windowIndices.map(idx => fetchCiclo(idAllCiclos[idx]))
                );
                setCiclosData(results);
            } finally {
                setLoad(false);
            }
        }
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── filler height calculation ────────────────────────────────────────
       A4 landscape content height (210mm - 2×8mm margins) in CSS px at 96dpi.
       After data loads, measure the table and pad it to the next page boundary. */
    useEffect(() => {
        if (!tableRef.current) return;
        const PAGE_H = (210 - 16) * (96 / 25.4); // ≈ 733.7 px
        const h      = tableRef.current.offsetHeight;
        const rem    = h % PAGE_H;
        setFillerHeight(rem > 1 ? Math.round(PAGE_H - rem) : 0);
    }, [ciclosData]);

    const handlePrint = useReactToPrint({
        pageStyle: `@page { size: A4 landscape; margin: 8mm 6mm; }
                    @media print { body { -webkit-print-color-adjust: exact; } }`,
        content: () => ref.current,
    });

    if (load) {
        return (
            <>
                <Header />
                <Main><Loading /></Main>
                <GlobalStyle />
            </>
        );
    }

    /* ── helpers ──────────────────────────────────────────────────────── */
    const getCicloLabel = (idx: number) => (total - idx) + 'º Ciclo';

    const bgColorSelo = (selo: any) => {
        switch (Number(selo)) {
            case 1: return '#f35f60';
            case 2: return '#fed458';
            case 3: return '#23c88f';
            case 4: return 'White';
            default: return 'white';
        }
    };

    /* ── borders ──────────────────────────────────────────────────────── */
    const B = '1px solid #000';
    /* every 7th day column gets a thicker right border */
    const dayBR = (j: number): React.CSSProperties =>
        (j + 1) % 7 === 0 && (j + 1) < CHUNK_SIZE ? { borderRight: '2px solid #000' } : {};

    /* each cell draws only right + bottom */
    const base: React.CSSProperties = {
        borderRight: B, borderBottom: B,
        verticalAlign: 'middle', padding: '0 1px',
    };

    /* row-label cells (Símb. Dia Rela. Regra) */
    const lbl: React.CSSProperties = {
        ...base,
        fontSize: '0.6rem', fontWeight: 600, color: '#32465B',
        textAlign: 'center', whiteSpace: 'nowrap', width: 28, padding: '1px 2px',
    };

    /* writingMode shortcut — no transform, just changes flow direction */
    const VRL = 'sideways-lr' as React.CSSProperties['writingMode'];

    /* ── render ───────────────────────────────────────────────────────── */
    return (
        <>
            <Header />
            <Main>

                <div className="page spaceInfo">
                    <button onClick={handlePrint}>CLIQUE PARA IMPRIMIR OU SALVAR PDF</button>
                    <div>PDF: Escolha "Salvar como PDF" — Landscape A4.</div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                <div className="page" ref={ref} style={{ maxWidth: '1500px' }}>

                    <table ref={tableRef} cellSpacing="0" cellPadding="0"
                           style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0,
                                    fontSize: '0.65rem', tableLayout: 'fixed' }}>

                        {/* column widths: [cycle-title 28px] [row-label 28px] [CHUNK_SIZE day cols auto] */}
                        <colgroup>
                            <col style={{ width: 28 }} />
                            <col style={{ width: 28 }} />
                            {Array.from({ length: CHUNK_SIZE }, (_, j) => <col key={j} />)}
                        </colgroup>

                        <thead>
                            {/* ── Logo + date row (inside table so no page-break before table) ── */}
                            <tr>
                                <th colSpan={2 + CHUNK_SIZE}
                                    style={{ textAlign: 'left', paddingBottom: 6, border: 'none', background: 'transparent' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                        <img height={28} src={logo} alt="Método Billings" />
                                        <span style={{ textAlign: 'right', textTransform: 'uppercase', fontSize: '0.7rem', color: '#434c5e', fontWeight: 400 }}>
                                            <strong>USUÁRIO: {userlogado.user?.nome} {userlogado.user?.conjuge ? `& ${userlogado.user.conjuge}` : ''}</strong><br />
                                            IMPRESSO EM {format(new Date(), "dd/MM/yyyy", { locale: ptBR })}
                                        </span>
                                    </div>
                                </th>
                            </tr>

                            {/* ── day-position header (1…CHUNK_SIZE) ────────── */}
                            <tr>
                                <th colSpan={2} style={{ ...base, borderLeft: B, borderTop: B, padding: 0 }}>
                                    Dia do Ciclo
                                </th>
                                {Array.from({ length: CHUNK_SIZE }, (_, j) => (
                                    <th key={j} style={{ ...base, ...dayBR(j), borderTop: B, textAlign: 'center',
                                                         fontSize: '0.7rem', color: '#32465B', padding: '1px 0' }}>
                                        {j + 1}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        {/* ── one group of rows per cycle, wrapped in chunks ── */}
                        <tbody>
                            {[...windowIndices].reverse().flatMap((idx, i) => {
                                const col       = windowIndices.length - 1 - i;
                                const cycleData = ciclosData[col] ?? [];
                                const label     = getCicloLabel(idx);
                                const isAntigo  = idx > 0;
                                const startDate = cycleData.length > 0
                                    ? format(parseISO(cycleData[0].dia), "dd/MM/yy", { locale: ptBR })
                                    : '—';

                                /* How many 35-day chunks this cycle needs */
                                const numChunks = Math.max(1, Math.ceil(cycleData.length / CHUNK_SIZE));

                                return Array.from({ length: numChunks }, (_, ci) => {
                                    const chunkStart = ci * CHUNK_SIZE;
                                    const isFirst    = ci === 0;

                                    return (
                                        <React.Fragment key={`cycle-${col}-c${ci}`}>

                                            {/* ════ ROW 1: Símb. ════ */}
                                            <tr>
                                                {/* Cycle title — rowSpan=5 per chunk (avoids page-break rowSpan issues) */}
                                                <td rowSpan={5}
                                                    style={{ ...base,
                                                             height: '227px',
                                                             borderLeft: B, borderTop: B,
                                                             writingMode: VRL,
                                                             textAlign: 'center',
                                                             verticalAlign: 'middle',
                                                             background: '#f5f5f5',
                                                             padding: '4px 2px',
                                                             overflow: 'hidden' }}>
                                                    {isFirst ? (
                                                        <>
                                                            <span style={{
                                                                fontWeight: 'bold', fontSize: '0.7rem',
                                                                textTransform: 'uppercase',
                                                                color: isAntigo ? '#32465B' : '#FF50A7',
                                                            }}>
                                                                {label}
                                                            </span>
                                                            {' '}
                                                            <span style={{
                                                                fontSize: '0.7rem',
                                                                textTransform: 'uppercase',
                                                            }}>
                                                                &nbsp; INÍCIO EM {startDate}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span style={{
                                                            fontSize: '0.65rem',
                                                            color: isAntigo ? '#32465B' : '#FF50A7',
                                                            textTransform: 'uppercase',
                                                        }}>
                                                            CONTINUAÇÃO {label}  
                                                            <span style={{ fontWeight: 'bold', transform: 'rotate(90deg) scale(1.5)', marginBottom: 4, color: '#000', display: 'inline-block'  }}>↪</span>
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Símb. label */}
                                                <td style={{ ...lbl, borderTop: B }}>Símb.</td>

                                                {/* Symbol cells */}
                                                {Array.from({ length: CHUNK_SIZE }, (_, j) => {
                                                    const day = cycleData[chunkStart + j];
                                                    return (
                                                        <td key={j}
                                                            style={{ ...base, ...dayBR(j),
                                                                     borderTop: B,
                                                                     textAlign: 'center', padding: '1px 0',
                                                                     backgroundColor: day?.id_mob ? bgColorSelo(day.selo) : undefined }}>
                                                            {day?.id_mob && (
                                                                <div style={{ transform: 'scale(0.85)',
                                                                              display: 'flex', justifyContent: 'center', alignItems: 'center',
                                                                              marginTop: -3, marginBottom: -3 }}>
                                                                    <SquemaSimbols
                                                                        edit={false}
                                                                        regra={day.regra}
                                                                        regracount={day.regracount}
                                                                        fertilidade={day.fertilidade}
                                                                        relacao={day.relacao}
                                                                        selo={day.selo}
                                                                        impresso={true}
                                                                        click={() => {}}
                                                                    />
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>

                                            {/* ════ ROW 2: Dia ════ */}
                                            <tr>
                                                <td style={lbl}>Dia</td>
                                                {Array.from({ length: CHUNK_SIZE }, (_, j) => {
                                                    const day = cycleData[chunkStart + j];
                                                    return (
                                                        <td key={j} style={{ ...base, ...dayBR(j), textAlign: 'center', padding: '1px 0' }}>
                                                            {day && (
                                                                <DiadoMes>
                                                                    <span style={{ fontSize: '0.7rem', lineHeight: '0.7rem' }}>
                                                                        {format(parseISO(day.dia), "dd")}
                                                                    </span>
                                                                    <small style={{ fontSize: '0.5rem', lineHeight: '0.4rem' }}>
                                                                        {format(parseISO(day.dia), "MMM", { locale: ptBR })}
                                                                    </small>
                                                                </DiadoMes>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>

                                            {/* ════ ROW 3: Sensação ════ */}
                                            <tr>
                                                <td style={{ ...lbl, writingMode: VRL,
                                                             height: SENS_H, textAlign: 'center',
                                                             padding: '2px 1px' }}>
                                                    Sensação / Observação
                                                </td>
                                                {Array.from({ length: CHUNK_SIZE }, (_, j) => {
                                                    const day = cycleData[chunkStart + j];
                                                    const txt = day?.id_mob
                                                        ? (Number(day.sensacao) === 0
                                                            ? day.descoutrasensacao
                                                            : day.descsensacao)
                                                        : null;
                                                    const obs = day?.id_mob ? (day.obssensacao || null) : null;
                                                    return (
                                                        <td key={j}
                                                            style={{ ...base, ...dayBR(j), padding: 0,
                                                                     position: 'relative',
                                                                     height: SENS_H,
                                                                     overflow: 'hidden' }}>
                                                            {(txt || obs) && (
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    bottom: 2,
                                                                    left: 2,
                                                                    writingMode: VRL,
                                                                    fontSize: '0.55rem',
                                                                    lineHeight: 1.1,
                                                                    maxHeight: SENS_H - 4,
                                                                    overflow: 'hidden',                                                                    
                                                                }}>
                                                                    {txt && (
                                                                        <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                                            {txt}
                                                                        </span>
                                                                    )}
                                                                    {obs && (
                                                                        <span style={{ textTransform: 'uppercase' }}>
                                                                            {txt ? ': ' : ''}{obs}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>

                                            {/* ════ ROW 4: Rela. ════ */}
                                            <tr>
                                                <td style={lbl}>Rela.</td>
                                                {Array.from({ length: CHUNK_SIZE }, (_, j) => {
                                                    const day = cycleData[chunkStart + j];
                                                    return (
                                                        <td key={j} style={{ ...base, ...dayBR(j), textAlign: 'center' }}>
                                                            {day?.id_mob && (
                                                                <div style={{ transform: 'scale(0.75)',
                                                                              display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                                    <Simbols edit={false} comp='relacao' regra={day.relacao} click={() => {}} />
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>

                                            {/* ════ ROW 5: Regra ════ */}
                                            <tr>
                                                <td style={lbl}>Regra</td>
                                                {Array.from({ length: CHUNK_SIZE }, (_, j) => {
                                                    const day = cycleData[chunkStart + j];
                                                    return (
                                                        <td key={j} style={{ ...base, ...dayBR(j), textAlign: 'center' }}>
                                                            {day?.id_mob && (
                                                                <div style={{ transform: 'scale(0.85)' }}>
                                                                    <Simbols edit={false} comp='regradia' regra={day.regradia} click={() => {}} />
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>

                                        </React.Fragment>
                                    );
                                });
                            })}
                            {/* Filler row — pads to the next page boundary */}
                            {fillerHeight > 0 && (
                                <tr style={{ height: fillerHeight }}>
                                    <td colSpan={2 + CHUNK_SIZE} style={{ borderRight: B, borderBottom: B }} />
                                </tr>
                            )}
                        </tbody>

                    </table>
                </div>
                </div>

            </Main>
            <GlobalStyle />
        </>
    );
};

export default GeraImpresso3Ciclos;
